import { useListOfficesApiV1AdminOfficesGet } from '../../../generated/api/admin/admin';
import {
  useGroups as useUserManagementGroups,
  useCreateGroup,
} from '@/shared/users/hooks/useGroups';
import type { Group } from '@/shared/users/types';

interface Office {
  id: string;
  name: string;
  description?: string | null;
  city?: string | null;
  country_code?: string | null;
  is_active?: boolean;
}

interface OfficesResponse {
  data: Office[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export function useOffices() {
  // Use the admin offices API (physical office locations)
  const query = useListOfficesApiV1AdminOfficesGet();

  // Transform the response to match expected format
  return {
    ...query,
    data: query.data as OfficesResponse | undefined,
  };
}

export function useDepartments() {
  return useUserManagementGroups({ type: 'department' });
}

/**
 * Create a department from inside People.
 *
 * A department is a `user_groups` row of type `department` — the same records
 * `/admin/groups?type=department` manages. This wrapper takes the name alone so
 * the `type` cannot be forgotten at a call site and a group land in the tenant's
 * group list that People will never list back.
 *
 * `POST /groups` is `require_admin`, so callers gate the affordance on
 * `user.is_admin` rather than letting a member discover the rule as a 403.
 */
export function useCreateDepartment() {
  const createGroup = useCreateGroup();

  return {
    createDepartment: (name: string): Promise<{ data: Group }> =>
      createGroup.mutateAsync({ name, type: 'department' }),
    isPending: createGroup.isPending,
  };
}
